/* eslint-disable complexity */
/* eslint-disable max-lines */
import { ActionButtonText } from '@strata/react-buttons';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FormSection } from '@strata/react-form-section';
import { DetailValue } from '@strata/react-label-value-pairs';
import { MessagingBlock } from '@strata/react-messaging-block';
import {
    ACCEPTED_QUESTION_ID,
    type Answer,
    AnsweredQuestionTree,
    type Question,
    QuestionForm,
} from '@strata/react-question-tree';
import { Timeline } from '@strata/react-timeline';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik, type FormikProps } from 'formik';
import { type ComponentProps, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { CreateEpaDialog } from './dialogs/CreateEpaDialog';

import { CancelPARequestDialog } from 'components/intakeCases/dialogs/CancelPARequestDialog';
import { Documents } from 'components/intakeCases/Documents';
import { EpaCaseDetailFields } from 'components/intakeCases/EpaCaseDetailFields';
import { PriorAuthUnsavedChangesSync } from 'components/intakeCases/PriorAuthUnsavedChangesSync';
import {
    Determination,
    DeterminationToStatusMessageMap,
    QuestionsAvailableMessage,
    QuestionSetBodyDisplayStrings,
    QuestionSetHeaderDisplayStrings,
    TransactionType,
} from 'services/constants';
import { mapRawAnswersToFormikValues } from 'services/helpers';
import { type EPaCase, type EPaResponse, usePostEpaCaseMutation } from 'store/api/epaApi';
import { type IntakeCase, type MappedMilestone } from 'store/api/intakeCaseApi';

const ValidationSchema = Yup.object({
    answers: Yup.array<object, Answer>().required().min(1),
    questionSetCompleted: Yup.bool().required(),
    attachment: Yup.array<object, File>(),
});

type PriorAuthFormValues = Yup.InferType<typeof ValidationSchema>;

/** Stable fallback so `nodes ?? []` does not allocate a new array every render (breaks Formik `enableReinitialize`). */
const EMPTY_QUESTION_NODES = [] as Question[];
interface PriorAuthUnsavedChangesState {
    hasUserChanges: boolean;
    isSubmitting: boolean;
    onSave: () => Promise<boolean>;
    onDiscard: () => void;
}

interface PriorAuthProps {
    intakeCaseDetails?: IntakeCase;
    intakeCaseDetailsFetching: boolean;
    intakeCaseMilestonesData: MappedMilestone[];
    intakeCaseId: string;
    epaCases?: EPaCase[];
    epaCasesFetching: boolean;
    locked?: boolean;
    onUnsavedChangesStateChange?: (state: PriorAuthUnsavedChangesState) => void;
}

const mapResponseToStatusMessage = (response: EPaResponse | undefined) => {
    if (!response) {
        return undefined;
    }
    if (response.transactionType === TransactionType.PA_CANCEL_RESPONSE) {
        return DeterminationToStatusMessageMap[TransactionType.PA_CANCEL_RESPONSE];
    }
    return DeterminationToStatusMessageMap[
        response.determination as keyof typeof DeterminationToStatusMessageMap
    ];
};

export const PriorAuth = ({
    intakeCaseDetails = undefined,
    intakeCaseDetailsFetching,
    intakeCaseMilestonesData,
    intakeCaseId,
    epaCases = [],
    epaCasesFetching,
    locked = false,
    onUnsavedChangesStateChange = undefined,
}: PriorAuthProps) => {
    const [isCancelPARequestDialogOpen, setIsCancelPARequestDialogOpen] = useState(false);
    const [isNewEpaRequestDialogOpen, setIsNewEpaRequestDialogOpen] = useState(false);
    const [hasSubmittedQuestionSet, setHasSubmittedQuestionSet] = useState(false);

    const [postEpaCase] = usePostEpaCaseMutation();

    const epaCase = epaCases?.at(-1);
    const allResponses = epaCase?.responses ?? [];
    const activeResponse = allResponses?.at(-1);
    const canCreateNewEpa =
        activeResponse?.determination === Determination.CLOSED ||
        activeResponse?.determination === Determination.DENIED;
    const questionSetNodes = activeResponse?.questionSet?.nodes;
    const nodes = questionSetNodes ?? EMPTY_QUESTION_NODES;
    const priorAuthNotInitiated = !epaCase || allResponses.length === 0;
    const hasQuestionSet = nodes.length > 0;
    const hasAnswers = (activeResponse?.questionSet?.answers?.length ?? 0) > 0;

    const statusMessage = mapResponseToStatusMessage(activeResponse);
    const { milestoneType } = statusMessage ?? { milestoneType: undefined };

    const isCaseOpen = activeResponse?.determination === Determination.OPEN;
    const questionSetCompletedInitial = !!activeResponse?.isComplete;
    const isQuestionSetReadOnly = questionSetCompletedInitial || hasSubmittedQuestionSet;

    const renderQuestionSetContent = (formik: FormikProps<PriorAuthFormValues>) => {
        // If there is no PAInitiationRequest, show messaging indicating that there is no PA request
        if (priorAuthNotInitiated) {
            return (
                <MessagingBlock
                    size={MessagingBlock.Sizes.MEDIUM}
                    primaryText={QuestionSetHeaderDisplayStrings.NO_PA_REQUEST}
                    secondaryText={QuestionSetBodyDisplayStrings.NO_PA_REQUEST}
                />
            );
        }

        if (isQuestionSetReadOnly) {
            return <AnsweredQuestionTree questions={nodes} answers={formik.values.answers} />;
        }

        return (
            <>
                <QuestionForm
                    nodes={nodes}
                    answers={formik.values.answers}
                    onSectionSubmit={async (answers) => {
                        await formik.setValues({
                            ...formik.values,
                            questionSetCompleted: false,
                            answers,
                        });
                    }}
                    onFormCompleted={async () => {
                        await formik.setFieldValue('questionSetCompleted', true);
                    }}
                />
                {formik.values.questionSetCompleted && (
                    <div style={{ maxWidth: 500 }}>
                        <h3 style={{ fontWeight: 600, marginTop: 0, padding: 0 }}>
                            Optional Attachment
                        </h3>
                        <FileUploadFormik
                            id="documents"
                            label="Add Attachment"
                            helperText="Adding an attachment can increase the time it takes for a PBM to come to a determination."
                            accept="application/pdf"
                            maxAllowedFiles={1}
                            onAddFiles={async (files) => {
                                await formik.setFieldValue('attachment', [
                                    ...(formik.values.attachment ?? []),
                                    ...files,
                                ]);
                            }}
                            onRemoveFile={async (filename) => {
                                await formik.setFieldValue(
                                    'attachment',
                                    formik.values.attachment?.filter(
                                        (file: File) => file.name !== filename,
                                    ),
                                );
                            }}
                        />
                    </div>
                )}
            </>
        );
    };

    const isEditableQuestionSet = hasQuestionSet && !isQuestionSetReadOnly;

    const priorAuthInitialValues = useMemo<PriorAuthFormValues>(() => {
        const answers = mapRawAnswersToFormikValues(
            activeResponse?.questionSet?.answers,
            questionSetNodes,
        );
        return {
            answers,
            questionSetCompleted:
                questionSetCompletedInitial ||
                answers?.at(-1)?.nextQuestionId === ACCEPTED_QUESTION_ID,
            attachment: undefined,
        };
    }, [activeResponse?.questionSet?.answers, questionSetCompletedInitial, questionSetNodes]);

    return (
        <div className="sds_intake-case-details">
            <CancelPARequestDialog
                isOpen={isCancelPARequestDialogOpen}
                onClose={() => setIsCancelPARequestDialogOpen(false)}
                epaCaseId={epaCase?.epaCaseId ?? ''}
                intakeCaseId={intakeCaseId}
            />
            <CreateEpaDialog
                isOpen={isNewEpaRequestDialogOpen}
                onClose={() => setIsNewEpaRequestDialogOpen(false)}
                intakeCaseId={intakeCaseId}
            />
            <div className="sds_intake-case-details__column" style={{ flexGrow: 1.35 }}>
                <Formik
                    initialValues={priorAuthInitialValues}
                    enableReinitialize
                    validateOnMount
                    validationSchema={ValidationSchema}
                    onSubmit={async ({ ...values }, { resetForm }) => {
                        if (activeResponse) {
                            await postEpaCase({
                                transactionId: activeResponse.transactionId,
                                nodes,
                                answers: values.answers,
                                isComplete: values.questionSetCompleted,
                                intakeCaseId,
                                attachment: values.attachment,
                            }).unwrap();
                            SuccessToast({
                                message: `Question set ${values.questionSetCompleted ? 'submitted' : 'saved'} successfully.`,
                            });
                            if (values.questionSetCompleted) {
                                setHasSubmittedQuestionSet(true);
                            }
                            resetForm({
                                values: {
                                    ...values,
                                    attachment: undefined,
                                },
                            });
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            formTitle="Question Set"
                            isLoading={epaCasesFetching}
                            primaryButtonLabel="Submit"
                            showPrimaryButton={hasQuestionSet && !isQuestionSetReadOnly}
                            secondaryButtonLabel="Save"
                            showSecondaryButton={hasQuestionSet && !isQuestionSetReadOnly}
                            secondaryButtonDisabled={
                                formik.isSubmitting || !formik.isValid || locked
                            }
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={
                                formik.isSubmitting ||
                                !formik.isValid ||
                                !formik.values.questionSetCompleted ||
                                locked // Prevent submitting if intake case is not assigned to user
                            }
                            onSecondaryButtonClick={() => {
                                void (async () => {
                                    await formik.setFieldValue('questionSetCompleted', false);
                                    await formik.submitForm();
                                })();
                            }}
                            formFooterLeftContent={
                                <>
                                    {isCaseOpen ? (
                                        <ActionButtonText
                                            label="Cancel PA Request"
                                            onClick={() => setIsCancelPARequestDialogOpen(true)}
                                        />
                                    ) : undefined}
                                    {canCreateNewEpa ? (
                                        <ActionButtonText
                                            label="Create New Epa Request"
                                            onClick={() => setIsNewEpaRequestDialogOpen(true)}
                                        />
                                    ) : undefined}
                                </>
                            }
                            formLeftContent={
                                <>
                                    {onUnsavedChangesStateChange && (
                                        <PriorAuthUnsavedChangesSync
                                            isEditableQuestionSet={isEditableQuestionSet}
                                            onUnsavedChangesStateChange={
                                                onUnsavedChangesStateChange
                                            }
                                        />
                                    )}
                                    <div className="sds_intake-case-details__epa-details">
                                        {hasQuestionSet && !hasAnswers && (
                                            <div className="margin__bottom--medium">
                                                <DetailValue value={QuestionsAvailableMessage} />
                                            </div>
                                        )}
                                        <EpaCaseDetailFields
                                            milestoneType={milestoneType}
                                            activeResponse={activeResponse}
                                            intakeCaseDetails={intakeCaseDetails}
                                        />
                                    </div>
                                    {renderQuestionSetContent(formik)}
                                </>
                            }
                        />
                    )}
                </Formik>
            </div>
            <div className="sds_intake-case-details__column">
                <Documents
                    intakeCaseDetails={intakeCaseDetails}
                    isLoading={intakeCaseDetailsFetching}
                />
                <div className="sds_details-page-overview__container" style={{ flex: '1 0' }}>
                    <FormSection
                        sectionTitle="Intake Case Timeline"
                        isLoading={intakeCaseDetailsFetching}
                        content={
                            <Timeline
                                sectionDataArray={
                                    intakeCaseMilestonesData as ComponentProps<
                                        typeof Timeline
                                    >['sectionDataArray']
                                }
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
};
